import { DEFAULT_FIXED_SIZE, DEFAULT_FONT_SIZE, DEFAULT_GLOBAL_TEXT_SCALE } from '@/lib/constants/labels'
import type { LabelRenderData } from '@/lib/three-d-resources/VueRefRenderer'
import { VueRefRenderer } from '@/lib/three-d-resources/VueRefRenderer'
import { labelsScaleFactor } from '@/lib/three-d-resources/utils'
import type { DeepReadonly, Ref } from 'vue'
import type { Object3D, PerspectiveCamera, Scene, WebGLProgramParametersWithUniforms } from 'three'
import {
  BufferGeometry,
  Group,
  Line,
  LineBasicMaterial,
  Material,
  Mesh,
  MeshBasicMaterial,
  NoBlending,
  SphereGeometry,
  Vector3,
} from 'three'
import type { ModelView } from '~/entities/meeting'
import type { ApiLabel, LabelVisibility } from '~/entities/model3d'
import { rectangleRounded } from '../three-d-editor/utils'
import { disposeNode, disposeScene } from '../three-d-viewer/utils'
import type { SceneConfig } from './scene-config'
import { createMeshThumbnailMaterial } from '@/lib/three-d-resources/utils'
import type { ModelNode } from '@/entities/model3d'

export type Label3DProperties = {
  labelGroup: Group
  cutout: Mesh
  flagpole: Line
  grabbable: Mesh
  width?: number
  height?: number
}

export interface TextLabel {
  id: string
  persistedId?: number
  modelId?: number
  point: Vector3
  content: string
  visible: boolean
  isNewlyCreated: boolean
  isEditing: boolean
  nodeId: number
  offsetX: number
  offsetY: number
  offsetZ: number
  flagpoleVisible: boolean
  flagpoleIndex: number
  textSize: number
  autoScaleSize: number
  refreshKey: number
  autoScale: boolean
}

type LabelsManagerMode = 'edit' | 'view'

export const CUTOUT_NAME = 'cutout'

export class LabelsManager {
  globalTextScale: number = DEFAULT_GLOBAL_TEXT_SCALE
  labelsObjectMap = new Map<string, Label3DProperties>()

  private labelsPass: VueRefRenderer
  private meshCutoutMaterial = new MeshBasicMaterial({ color: 0x000000 })
  //TODO remove + memory leak
  private meshCutoutGeometry = rectangleRounded(1, 1, 0.1, 10)
  private flagpoleMaterial = new LineBasicMaterial({ color: 0xffffff })
  private flagpoleGrabbableGeometry = new SphereGeometry(150, 8, 4, 0, Math.PI * 2, 0, Math.PI * 0.5) // the top half of a sphere
  private flagpoleGrabbableMaterial = new MeshBasicMaterial({ color: 0xffffff, visible: false })
  private labels = new Map<string, TextLabel>()
  private sceneConfig: SceneConfig | null = null
  private modelId: number | null = null
  private meshThumbnailMaterial: Material | null = null
  public mode: Ref<LabelsManagerMode> = ref('view')

  group = new Group()
  labelsSpec = ref<LabelRenderData[]>([])

  constructor(
    private loaderConfig: { uuid: string | null },
    private rebuildLayerGroups: () => void,
    private onViewUpdated: () => void,
    private onVisibilityChanged?: (payload: LabelVisibility[]) => void,
    mode: LabelsManagerMode = 'view',
    private currentView?: Ref<DeepReadonly<ModelView>>
  ) {
    this.labelsPass = new VueRefRenderer(this.labelsSpec)

    // label cutout
    this.meshCutoutMaterial.opacity = 0
    this.meshCutoutMaterial.transparent = true
    // NoBlending allows the GL plane to occlude the CSS plane
    this.meshCutoutMaterial.blending = NoBlending
    this.meshThumbnailMaterial = createMeshThumbnailMaterial()

    this.group.name = 'labels'
    this.mode.value = mode
  }

  setSceneConfig(sceneConfig: SceneConfig) {
    this.sceneConfig = sceneConfig
    this.resetVisibilities()
    this.onViewUpdated()
  }

  setMode(mode: LabelsManagerMode): void {
    this.mode.value = mode
  }

  async fetchLabelsForModel(modelId: number, layersMap: Map<number, ModelNode>) {
    this.modelId = modelId
    const data = await $fetch<ApiLabel[]>(`/v2/3dmodel/${modelId}/labels`, {
      ...useFetchSharedOptions(),
      headers: { ...useFetchSharedOptions().headers, 'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}` },
    })
    if (this.modelId !== modelId) return

    disposeScene(this.group)

    const refreshKey = Date.now()

    this.labels = new Map(
      data.map(l => [
        `${l.id}`,
        {
          id: `${l.id}`,
          persistedId: l.id,
          modelId: l.model3d_id,
          point: new Vector3(l.position_x, l.position_y, l.position_z),
          offsetX: l.offset_x,
          offsetY: l.offset_y,
          offsetZ: l.offset_z,
          content: l.text,
          visible: this.isLabelVisible(`${l.id}`),
          isNewlyCreated: false,
          isEditing: false,
          nodeId: l.node_id,
          flagpoleVisible: l.flagpole_visible,
          flagpoleIndex: l.flagpole_index,
          textSize: l.style?.text_size ?? DEFAULT_FONT_SIZE,
          autoScaleSize: l.style?.auto_scale_size ?? DEFAULT_FIXED_SIZE,
          autoScale: l.auto_scale,
          refreshKey,
        },
      ])
    )
    this.createAllLabelMeshes()
    this.rebuildLayerGroups()
    this.onViewUpdated()
  }

  rebuildLabels(textScale: number = 1) {
    const refreshKey = Date.now()

    this.labels.forEach(l => {
      const labelPayload = {
        ...l,
        textSize: l.textSize * textScale,
        refreshKey,
      }

      this.labels.set(`${l.id}`, labelPayload)
    })

    this.createAllLabelMeshes()
    this.rebuildLayerGroups()
    this.onViewUpdated()
  }

  getLabel(id: string): TextLabel | undefined {
    return this.labels.get(id)
  }

  getLabels(): TextLabel[] {
    return [...this.labels.values()]
  }

  getLabelObject(id: string): Object3D | undefined {
    return this.labelsObjectMap.get(id)?.labelGroup
  }

  getLabelsForNode(nodeId: number): TextLabel[] {
    return [...this.labels.values()].filter(l => l.nodeId === nodeId)
  }

  addLabel(label: TextLabel): void {
    this.labels.set(label.id, label)
    this.createLabelSceneObjects({ label })
    this.rebuildLayerGroups()
  }

  setVisible(id: string, visible: boolean) {
    this.updateLabel(id, { visible })
    this.onVisibilityChanged?.([{ labelId: id, visible }])
  }

  private isLabelVisible(labelId: string): boolean {
    if (this.currentView?.value.layer_visibilities.length) {
      const visibility = this.currentView.value.layer_visibilities.find(v => v.layer_id === labelId && v.type === 'text-label')
      if (visibility) return visibility.visible
    }

    return this.sceneConfig?.isLabelVisible(labelId) ?? false
  }

  updateLabel(id: string, updates: Partial<TextLabel>): TextLabel | undefined {
    const currentLabel = this.labels.get(id)

    if (!currentLabel) return

    const newLabel = { ...currentLabel, refreshKey: Date.now(), ...updates }

    if (!newLabel.content && !newLabel.isNewlyCreated) {
      this.removeLabel(id)
      return
    }

    if (newLabel.visible !== currentLabel.visible) {
      const labelMesh = this.labelsObjectMap?.get(newLabel.id)
      if (labelMesh) {
        labelMesh.labelGroup.visible = newLabel.visible
        if (!newLabel.visible) {
          disposeNode(labelMesh.labelGroup)
        }
      }
    }

    this.labels.set(id, newLabel)

    this.rebuildLayerGroups()
    this.onViewUpdated()

    return newLabel
  }

  removeLabel(id: string): void {
    const label = this.labels.get(id)
    if (!label) return

    const labelToDelete = this.labelsObjectMap?.get(label.id)
    if (labelToDelete) {
      this.disposeLabel(label.id, labelToDelete)
    }

    this.labels.delete(id)

    this.rebuildLayerGroups()
  }

  private resetVisibilities() {
    this.labels.forEach((v, k, m) => {
      const visible = this.isLabelVisible(v.id)
      m.set(k, { ...v, visible })
      const labelMesh = this.labelsObjectMap.get(v.id)
      if (labelMesh) {
        labelMesh.labelGroup.visible = visible
        if (!visible) {
          disposeNode(labelMesh.labelGroup)
        }
      }
    })
  }

  toggleGroupVisibility(visible: boolean) {
    this.group.visible = visible
  }

  setGlobalTextScale(value: number) {
    this.globalTextScale = value
  }

  render = (_: Scene, camera: PerspectiveCamera) => {
    if (!this.labelsObjectMap) return
    this.labelsPass.render([...this.labels.values()], this.globalTextScale, camera, this.labelsObjectMap)
  }

  setSize(width: number, height: number) {
    // Ensure size is an integer and at least 1 x 1
    // Non-integer values can cause blurring
    width = Math.floor(Math.max(1, width))
    height = Math.floor(Math.max(1, height))

    this.labelsPass.setSize(width, height)
  }

  resizeCutout(id: string, width: number, height: number): void {
    const labelObject = this.labelsObjectMap.get(id)
    if (!labelObject) return

    labelObject.width = width
    labelObject.height = height

    const label = this.labels.get(id)
    const textSize = label?.textSize || DEFAULT_FONT_SIZE

    // Scale down text sizes > 100 which render poorly on Firefox.
    const factor = labelsScaleFactor([...this.labels.values()])

    const shouldAutoScale = label?.autoScale || false
    const newGeometry = rectangleRounded(width, height, textSize / 4 / (shouldAutoScale ? 1 : factor), 10, shouldAutoScale)
    newGeometry.computeBoundingSphere()
    labelObject.cutout.geometry.dispose()
    labelObject.cutout.geometry = newGeometry

    labelObject.cutout.scale.setScalar(this.globalTextScale * (shouldAutoScale ? 1 : factor))
    labelObject.cutout.updateMatrixWorld()
  }

  // currently used for thumbnails
  setAllLabelOpacity(value: number) {
    this.meshCutoutMaterial.opacity = value
  }

  resetData() {
    this.dispose()
    this.labelsObjectMap = new Map<string, Label3DProperties>()
    this.labels = new Map<string, TextLabel>()
    this.labelsSpec.value = []
    this.sceneConfig = null
    this.modelId = null
  }

  private createAllLabelMeshes = () => {
    this.dispose()
    for (const label of this.labels.values()) {
      this.createLabelSceneObjects({ label })
    }
    this.onViewUpdated()
  }

  private createLabelSceneObjects = ({ label }: { label: TextLabel }) => {
    const labelGroup = new Group()
    const properties = this.labelsObjectMap.get(label.id)
    const offset = new Vector3(label.offsetX, -label.offsetY, label.offsetZ)
    const position = new Vector3().copy(label.point)
    const finalPosition = new Vector3().addVectors(position, offset)
    const cutout = new Mesh(this.meshCutoutGeometry, this.meshCutoutMaterial)
    cutout.name = CUTOUT_NAME
    cutout.userData.labelId = label.id
    cutout.position.copy(finalPosition)

    // TODO: potential code smell, does this code run?
    if (properties) {
      cutout.scale.set(properties.width!, properties.height!, 1).multiplyScalar(this.globalTextScale)
    }

    const flagpoleGeometry = new BufferGeometry()
    flagpoleGeometry.setDrawRange(0, 2)
    const points = [position, finalPosition]
    flagpoleGeometry.setFromPoints(points)
    const flagpole = new Line(flagpoleGeometry, this.flagpoleMaterial)
    flagpole.name = 'flagpole'
    flagpole.visible = label.flagpoleVisible && label.visible

    // invisible sphere at flagpole base to make it easier to click and drag
    const grabbable = new Mesh(this.flagpoleGrabbableGeometry, this.flagpoleGrabbableMaterial)
    grabbable.userData.labelId = label.id
    grabbable.position.copy(position)
    grabbable.name = 'flagpoleGrabbable'

    labelGroup.add(cutout, flagpole, grabbable)
    this.group.add(labelGroup)
    const newProperties = { ...properties, labelGroup, cutout, flagpole, grabbable }
    this.labelsObjectMap.set(label.id, newProperties)
  }

  private dispose() {
    // remove all meshes and flagpoles
    if (this.labelsObjectMap) {
      for (const [id, objects] of this.labelsObjectMap) {
        this.disposeLabel(id, objects)
      }
    }
  }

  private disposeLabel(id: string, objects: Label3DProperties) {
    if (this.labelsObjectMap && this.group) {
      this.group.remove(objects.labelGroup)
      disposeNode(objects.labelGroup)
      this.labelsObjectMap.delete(id)
    }
  }

  applyThumbnailMaterial() {
    if (!this.labelsObjectMap || !this.meshThumbnailMaterial) return
    for (const [_, objects] of this.labelsObjectMap) {
      if (objects.cutout.material instanceof Material) {
        objects.cutout.material.dispose()
        objects.cutout.material = this.meshThumbnailMaterial
        objects.cutout.material.needsUpdate = true
      }
    }
  }

  applyCutoutMaterial() {
    if (!this.labelsObjectMap) return
    for (const [_, objects] of this.labelsObjectMap) {
      if (objects.cutout.material instanceof Material) {
        objects.cutout.material.dispose()
        objects.cutout.material = this.meshCutoutMaterial
        objects.cutout.material.needsUpdate = true
      }
    }
  }
}
